import _ from 'lodash';
import { v4 } from 'uuid';
import { useSnackbarContext } from 'Context/SnackbarContext';


/**
 * range
 * @param start Optional. The start of the range (inclusive)
 * @param stop Required. The end of the range (exclusive)
 * @returns number[] an array of a range up to the stop.
 *
 * Example:
 * // Returns [0,1,2,3,4,5,6,7,8,9]
 * range(10)
 *
 * // Returns [1,2,3,4,5]
 * range(1,6)
 */
const range = (start, stop) => {
  if (start === undefined && stop === undefined) {
    throw new Error('At least 1 param required');
  }

  if (stop === undefined) {
    return [...Array(start).keys()];
  }

  return [...Array(stop - start).keys()].map(v => v + start);
};

export { range };

/**
 * Returns an array of arrays of length windowSize containing overlapping sections of the original array. If the array's
 * length is less than windowSize, an empty array will be returned.
 * @param array {T[]}
 * @param windowSize {number}
 * @returns {[T[]...]}
 * @example
 *
 * const x = [1, 2, 3, 4, 5];
 * windows(x, 3);
 * // => [ [ 1, 2, 3 ], [ 2, 3, 4 ], [ 3, 4, 5 ] ]
 */
export const windows = (array, windowSize) => {
  const arr = [];

  if (array.length < windowSize) {
    return arr;
  }

  for (let i = 0; i + windowSize <= array.length; i++) {
    arr.push(array.slice(i, i + windowSize));
  }

  return arr;
};

/**
 * Returns a function that bounds a number between `min` and `max`, inclusive.
 * @param min {number}
 * @param max {number}
 * @returns {function(number): number}
 * @example
 *
 * const betweenFiveAndTen = bound(5, 10);
 * betweenFiveAndTen(1); // => 5
 * betweenFiveAndTen(7); // => 7
 * betweenFiveAndTen(11); // => 10
 */
export const bound = (min, max) => val => Math.min(Math.max(min, val), max);


/**
 * Given a number, returns its ordinal number (e.g. 1st, 2nd, etc.).
 * @param number {number} A number
 * @returns {string} Ordinal value
 * @example
 * ordinalizeNumber(21); // => '21st'
 */
export const ordinalizeNumber = (number) => {
  if (number > 3 && number < 21) {
    return `${number}th`;
  }

  switch (number % 10) {
    case 1:
      return `${number}st`;
    case 2:
      return `${number}nd`;
    case 3:
      return `${number}rd`;
    default:
      return `${number}th`;
  }
};

/** @deprecated -- execCommand('copy') is no longer widely supported on modern browsers and in particular this method
 * does not work on many modern mobile browsers. Use useCopyToClipboard instead * */
export const copyToClipboard = (text) => {
  const copy = document.createElement('textarea');
  copy.value = text;
  copy.setAttribute('readonly', '');
  copy.style = { position: 'absolute', left: '-9999px' };
  document.body.appendChild(copy);
  copy.select();
  copy.focus();
  document.execCommand('copy');
  document.body.removeChild(copy);
};

/** Copies the provided text to the clipboard with onSuccess and onError handler functions.
 * When using this function be aware that Safari has a requirement that
 * you cannot copy items to the clipboard as part of an async call -- the copy
 * must happen as the direct result of a button click or touch event.
 * See Security & Privacy in https://webkit.org/blog/10855/async-clipboard-api/ */
export const copyToClipboardV2 = async (text, onSuccess, onError) => {
  if (!navigator.clipboard) {
    copyToClipboard(text);
    onSuccess();
  } else {
    try {
      await navigator.clipboard.writeText(text);
      onSuccess();
    } catch (err) {
      onError(err);
    }
  }
};

/** Copies the provided text to the clipboard with onSuccess and onError handler functions.
 * When using this hook be aware that Safari has a requirement that
 * you cannot copy items to the clipboard as part of an async call -- the copy
 * must happen as the result of a button click or touch event.
 * See Security & Privacy in https://webkit.org/blog/10855/async-clipboard-api/ */
export const useCopyToClipboard = () => {
  const { displaySuccess, displayErrorMessage } = useSnackbarContext();

  const onSuccess = () => displaySuccess('Copied to clipboard!');
  const onError = (err) => displayErrorMessage(err, 'Unable to copy to clipboard!');

  const copyFn = (text) => copyToClipboardV2(text, onSuccess, onError);

  return { copyToClipboard: copyFn };
};

export const isValueEmpty = (value) => {
  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return value === undefined || value === null || value === '';
};

const getOrderByFunction = (type, attribute) => {
  switch (type) {
    case 'datetime':
    case 'date':
    case 'string': {
      return resource => resource.attr(attribute);
    }
    case 'number': {
      return resource => parseFloat(resource.attr(attribute));
    }

    case 'bool': {
      return resource => resource.attr(attribute) === 'false';
    }
    case 'multipicklist':
    case 'picklist': {
      return resource => resource.selectedOptions(attribute, 'prompt')[0];
    }
    default: {
      throw new Error(`no case to order type ${type}`);
    }
  }
};

/**
 * Given a collection, attribute name, and direction
 * return a ordered array of resources
 * @param collection {Collection} A Collection
 * @param attributeName {string} an attribute name
 * @param direction {'asc'|'desc'} asc or desc
 * @returns {Array<Resource>} An array of resources in order
 */
export function orderCollection(collection, attributeName, direction) {
  const meta = collection.meta();
  const field = meta.field(attributeName);
  const type = field.attr('type');

  const orderByFunc = getOrderByFunction(type, attributeName);

  return _(collection).toArray()
    .orderBy(orderByFunc, direction)
    .value();
}

export { partition, isIOSSafari } from './UtilsWithoutLodash';

/**
 * Format an array of strings to a human-readable list. Ex: ["blue", "red", "green"] yields "blue, red, and green". Thanks, Kent!
 * @param array {Array} An array of values of strings.
 * @param options {Object} Configuration
 * @returns {string} The formatted string
 */
export function listify(
  array,
  { conjunction = 'and ', stringify = item => item.toString() } = {},
) {
  return array.reduce((list, item, index) => {
    if (index === 0) return stringify(item);
    if (index === array.length - 1) {
      if (index === 1) return `${list} ${conjunction}${stringify(item)}`;
      return `${list}, ${conjunction}${stringify(item)}`;
    }
    return `${list}, ${stringify(item)}`;
  }, '');
}

/**
 * Strip out HTML tags from a Rich Text String
 * @param {string} string Rich text string to remove HTML tags from
 * @returns {string} HTML Tag free string
 */
export const stripHTML = string => {
  if (typeof string !== 'string') {
    return '';
  }
  return string.replace(/(<([^>]+)>)/gi, '');
};

const OPERATING_SYSTEMS = {
  windowsPhone: 'Windows Phone',
  android: 'Android',
  iOS: 'iOS',
  unknown: 'Unknown',
};

export const getMobileOperatingSystem = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(userAgent)) {
    return OPERATING_SYSTEMS.windowsPhone;
  }

  if (/android/i.test(userAgent)) {
    return OPERATING_SYSTEMS.android;
  }

  // iOS detection from: http://stackoverflow.com/a/9039885/177710
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return OPERATING_SYSTEMS.iOS;
  }

  return OPERATING_SYSTEMS.unknown;
};

export const isAndroid = () => {
  const OS = getMobileOperatingSystem();
  return OS === OPERATING_SYSTEMS.android;
};

export const getUUID = () => {
  return v4().substring(0, 2);
};

export const isNil = (val) => val === undefined || val === null;
